import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton as RocketIconButton } from '@totalsoft/rocket-ui';
import { Icon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const rocketTypes = ['add', 'cancel', 'delete', 'download', 'downward', 'edit', 'view', 'save', 'upward', 'expandLess', 'expandMore'];
const localTypes = ['backward', 'undo'];

const IconButton = ({ type, icon, iconFontSize = 'small', size = 'small', tooltip, onClick, ...rest }) => {
  const { t } = useTranslation();

  const internalType = useMemo(() => {
    if (icon) return;
    if (localTypes.includes(type)) return;
    if (!rocketTypes.includes(type)) return;
    return type;
  }, [type, icon]);

  const internalTooltip = useMemo(() => {
    if (tooltip || !type) return tooltip;
    switch (type) {
      case 'add':
        return t('Add');
      case 'cancel':
        return t('Cancel');
      case 'delete':
        return t('Delete');
      case 'download':
        return t('Download');
      case 'downward':
        return t('Downward');
      case 'edit':
        return t('Edit');
      case 'view':
        return t('View');
      case 'save':
        return t('Save');
      case 'upward':
        return t('Upward');
      case 'expandLess':
        return t('Collapse');
      case 'expandMore':
        return t('Expand');
      case 'backward':
        return t('BackToList');
      case 'undo':
        return t('UndoChanges');

      default:
        return;
    }
  }, [type, tooltip, t]);

  const internalIcon = useMemo(() => {
    if (!icon) {
      if (localTypes.includes(type)) {
        switch (type) {
          case 'backward':
            return <ArrowBackIcon fontSize={iconFontSize} />;
          case 'undo':
            return <ReplayIcon fontSize={iconFontSize} />;
          default:
            return;
        }
      }
      return;
    }
    if (typeof icon === 'string') return <Icon fontSize={iconFontSize}>{icon}</Icon>;
    return icon;
  }, [icon, type, iconFontSize]);

  return (
    <RocketIconButton
      color='primary'
      size={size}
      variant='text'
      type={internalType}
      tooltip={internalTooltip}
      fontSize={iconFontSize}
      onClick={onClick}
      {...rest}
    >
      {internalIcon}
    </RocketIconButton>
  );
};

IconButton.propTypes = {
  type: PropTypes.oneOf([
    'add',
    'cancel',
    'delete',
    'download',
    'downward',
    'edit',
    'view',
    'save',
    'upward',
    'expandLess',
    'expandMore',
    'undo'
  ]),
  icon: PropTypes.string,
  /**
   * Is only applied to local icon types (ex. 'undo') and icons passed as string
   */
  iconFontSize: PropTypes.oneOf(['inherit', 'small', 'medium', 'large']),
  size: PropTypes.string,
  tooltip: PropTypes.string,
  onClick: PropTypes.func
};

export default IconButton;
